import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { Text } from "components/Typography/text.styled";
import { FontSizeV2 } from "config/font";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  ActionsSlot,
  ControlsRow,
  DateFilterSlot,
  HeaderRow,
  Toolbar,
} from "./timeReportsToolbar.styled";

export type TimeReportsGroupByOption = "person" | "team";

interface TimeReportsToolbarProps {
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  personOptions: Array<{ value: string; label: string }>;
  unitTeamEmptyText: string;
  personEmptyText: string;
  selectedPersonIds: string[];
  monthDate: LocalDate;
  selectedRangeStart: LocalDate | null;
  selectedRangeEnd: LocalDate | null;
  isAllRowsExpanded: boolean;
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  onSelectedPersonIdsChange: (values: string[]) => void;
  onDateRangeChange: (dates: LocalDate[]) => void;
  onExpandAllClick: () => void;
  onOptionsClick: () => void;
}

export const TimeReportsToolbar: React.FC<TimeReportsToolbarProps> = (
  props,
) => {
  const { t } = useTranslation();

  return (
    <Toolbar>
      <HeaderRow>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/** TODO: Delete beta mark when tested and ready */}
          <Text fontSize={FontSizeV2.H2}>{t("TimeReports.TimeReports")}</Text>
          <Text
            fontSize={FontSizeV2.Small}
            style={{
              marginLeft: 10,
              marginTop: 20,
              background: "rgba(251, 193, 0, 0.3)",
              color: "#CF9F00",
              padding: 8,
              borderRadius: 10,
            }}
          >
            {"(Beta)"}
          </Text>
        </div>
      </HeaderRow>
      <ControlsRow>
        <UnitTeamDropdown
          label={t("TimeReports.UnitTeam")}
          placeholder={t("TimeReports.AllUnitsTeamsVisible")}
          emptyText={props.unitTeamEmptyText}
          options={props.unitTeamOptions}
          selectedTeamIds={props.selectedWorkGroupIds}
          onSelectedTeamIdsChange={props.onSelectedWorkGroupIdsChange}
        />
        <SearchableDropdown
          label={t("TimeReports.Person")}
          placeholder={t("TimeReports.SearchOrBrowse")}
          emptyText={props.personEmptyText}
          options={props.personOptions}
          leadingIconName="v2/search"
          multiSelect
          selectedValues={props.selectedPersonIds}
          onSelectedValuesChange={props.onSelectedPersonIdsChange}
          onClear={() => props.onSelectedPersonIdsChange([])}
        />
        <DateFilterSlot>
          <DatePicker
            key={`time-reports-date-${props.monthDate.toString()}`}
            label={t("CompanyTimeReport.Date")}
            allowRangePicker
            allowMultiSelectInSingleMode={false}
            allowClear
            value={props.selectedRangeStart}
            onRangeChange={props.onDateRangeChange}
            restrictToMonth={props.monthDate.withDayOfMonth(1)}
            disableNavigationOutsideMonth
          />
        </DateFilterSlot>
        <ActionsSlot>
          <SecondaryButton
            label={
              props.isAllRowsExpanded
                ? t("TimeReports.CollapseAll")
                : t("TimeReports.ExpandAll")
            }
            size="small"
            iconName="v2/expand-vertical"
            onClick={props.onExpandAllClick}
          />
          <SecondaryButton
            label={t("TimeReports.Options")}
            size="small"
            iconName="v2/settings"
            onClick={props.onOptionsClick}
          />
        </ActionsSlot>
      </ControlsRow>
    </Toolbar>
  );
};
